"use client";

import Badge from "@components/Badge/Badge";
import Link from "@components/Link/Link";
import { useAuth } from "@context/authContext";
import { useCartActions, useCartState } from "@context/cartContext";
import { useHeaderContext } from "@context/headerContext";
import { useSiteInfo } from "@context/siteInfoContext";
import { Button } from "@components/ngg";
import { CartIcon, HeartIcon, UserIcon } from "@ngg/icons";
import type { ReactNode } from "react";
import type { Dictionary } from "types";

import { envConfig } from "@/config/env";
import useEvents from "@app/_hooks/useEvents";
import { useWishlist } from "@app/_hooks/useWishlist";

export const LoginButton = ({
  locale,
  className,
  children,
  dictionary,
  redirectOnSuccess = true,
}: {
  locale?: string;
  className?: string;
  children: ReactNode;
  dictionary?: Dictionary;
  redirectOnSuccess?: boolean;
}) => {
  const { myPagesStartPage } = useSiteInfo();
  const { auth, showLogin } = useAuth();

  return auth?.user ? (
    <Button
      as={Link}
      locale={locale}
      href={myPagesStartPage?.slug}
      className={className}
      title={dictionary?.myPages}
      aria-label={dictionary?.myPages}
    >
      {children}
    </Button>
  ) : (
    <Button
      onClick={() => showLogin(redirectOnSuccess)}
      type="button"
      className={className}
      title={dictionary?.login}
      aria-label={dictionary?.login}
    >
      {children}
    </Button>
  );
};

const Actions = () => {
  const { viewCart } = useEvents();
  const { locale, dictionary } = useHeaderContext();
  const { wishlist } = useSiteInfo();
  const { cart } = useCartState();
  const { showCart } = useCartActions();
  const { total } = useWishlist();

  const numItemsInCart = cart?.items
    ?.filter(
      (item) =>
        item?.partNo !==
        `${envConfig.NEXT_PUBLIC_NSHIFT_DYNAMIC_FREIGHT_PARTNO}`,
    )
    ?.reduce((total, item) => total + (item?.quantity || 0), 0);

  const handleOnClick = () => {
    showCart();
    if (cart) viewCart(cart);
  };

  return (
    <div className="flex items-center">
      {envConfig.NEXT_PUBLIC_NORDICS_FEATURE_FLAG === "true" ? (
        <>
          <LoginButton
            locale={locale}
            className="hidden items-center p-1 md:flex [&>span]:hidden"
            dictionary={dictionary}
          >
            <UserIcon className="text-xl" />
          </LoginButton>
          <Link
            className="p-1"
            locale={locale}
            href={wishlist?.slug}
            title={dictionary?.wishlist}
            aria-label={dictionary?.wishlist}
          >
            <Badge badgeContent={total}>
              <HeartIcon className="text-xl" />
            </Badge>
          </Link>
        </>
      ) : null}
      <button
        className="p-1"
        type="button"
        onClick={handleOnClick}
        data-testid="cart-icon-button"
        title={dictionary?.yourCart}
        aria-label={dictionary?.yourCart}
      >
        <Badge badgeContent={numItemsInCart}>
          <CartIcon className="text-xl" />
        </Badge>
      </button>
    </div>
  );
};

export default Actions;
